<script setup lang="ts">
/**
 * Carte d'une boutique — rayon /recherche/boutiques, accueil.
 * Présentation « vitrine » commune (VitrineCarte, Karim 2026-10-12) : aperçu
 * des produits et leur nombre posés sur l'image.
 *
 * Lien réel (`<a href>`, dans VitrineCarte) et non un `@click` : Google doit
 * pouvoir suivre le lien, c'est tout l'intérêt d'avoir mis ces pages au
 * sitemap.
 */
import { themeAffiche } from "~/utils/themesAffiches";

/** Seuls les champs affichés : l'accueil passe une version allégée. */
interface BoutiqueCarte {
  slug: string;
  title: string;
  category?: string | null;
  cover_image?: string | null;
  organization_name?: string | null;
  organization_logo?: string | null;
  products?: Array<{ name?: string | null }>;
}

const props = defineProps<{ shop: BoutiqueCarte; priority?: boolean }>();

const nomsProduits = computed(() =>
  (props.shop.products ?? []).map((p) => p.name ?? "").filter(Boolean),
);
/** « 8 produits » — le chiffre qui dit à l'acheteur si ça vaut le clic. */
const nombreProduits = computed(() => nomsProduits.value.length);

/** Thème ; rien pour « Autre » (n'apprend rien). */
const pastille = computed(() =>
  props.shop.category && props.shop.category !== "autre" ? themeAffiche(props.shop.category) : "",
);
</script>

<template>
  <VitrineCarte
    :href="`/boutique/${shop.slug}`"
    :titre="shop.title"
    :image="shop.cover_image ?? null"
    :pastille="pastille"
    :association-nom="shop.organization_name ?? undefined"
    :association-logo="shop.organization_logo ?? null"
    :action="$t('commun.vitrine.actionBoutique')"
    :priority="priority"
  >
    <div class="flex items-center justify-between gap-3">
      <p class="min-w-0 truncate text-sm text-white/75">
        {{ nomsProduits.slice(0, 3).join(" · ") }}
      </p>
      <span
        v-if="nombreProduits > 0"
        class="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-sm text-[#0c3097]"
        style="font-weight: 700 !important"
      >
        <UIcon name="i-lucide-shopping-bag" class="size-4" />
        {{ $t("commun.vitrine.produits", { n: nombreProduits }, nombreProduits) }}
      </span>
    </div>
  </VitrineCarte>
</template>
